import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Chip, chipClasses } from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import { styled } from "@mui/material/styles";
import SvgIcon from "@mui/material/SvgIcon";
import * as React from "react";
import { KNOWLEDGE_SOURCES, VALIDATIONS_SEVERITY } from "src/components/collections/consts";
import { getValidationMessage } from "src/components/collections/helpers";
import HyroTruncateTypography from "@hyro-components/hyro-truncate-typography/HyroTruncateTypography";
import { MandatoryBadge } from "@hyro-components/MandatoryBadge";
import {
  COLLAPSED_NAV_ITEM_BORDER_RADIUS,
  COLLAPSED_NAV_ITEM_SIZE,
  NAV_ICON_BOX_SIZE,
  NAV_ICON_GLYPH_SIZE,
  NAV_ITEM_HEIGHT,
  NAV_LABEL_MAX_WIDTH,
  NAVIGATION_DOM_IDS,
  NAVIGATION_TEST_IDS,
  hasOnlyChild
} from "./NavigationBar.consts";
import useAppStore from "../../stores/appStore";
import { hasKnowledgeSourceValidationErrorsSelector, hasKnowledgeValidationErrorsSelector, validationResultsSelector } from "../../stores/selectors/collectionsSelectors";
import { systemModeSelector } from "../../stores/selectors/appSelectors";
import { SYSTEM_MODES } from "../../stores/slices/createAppSlice";

const StyledChip = styled(Chip)`
  width: 46px;
  height: 20px;
  margin-left: 4px;
  .${chipClasses.label} {
    padding: 0;
  }

  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.16px;
`;

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== "open" && prop !== "isChild"
})(({ theme, open, isChild }) => ({
  "minHeight": NAV_ITEM_HEIGHT,
  "gap": theme.spacing(1.5),
  "padding": `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  "borderRadius": theme.shape.borderRadius,
  "color": theme.palette.text[700],
  "&:hover": isChild
    ? { backgroundColor: "transparent" }
    : {
      backgroundColor: theme.palette.system[50],
      color: theme.palette.text[800]
    },
  "&.Mui-selected": {
    "color": theme.palette.primary[500],
    "backgroundColor": theme.palette.tertiary[100],
    "&:hover": {
      backgroundColor: theme.palette.tertiary[100],
      color: theme.palette.primary[500]
    }
  },
  ...(!open && {
    width: COLLAPSED_NAV_ITEM_SIZE,
    height: COLLAPSED_NAV_ITEM_SIZE,
    minHeight: COLLAPSED_NAV_ITEM_SIZE,
    padding: 0,
    justifyContent: "center",
    borderRadius: COLLAPSED_NAV_ITEM_BORDER_RADIUS
  })
}));

const StyledListItemIcon = styled(ListItemIcon)({
  "color": "inherit",
  "minWidth": 0,
  "width": NAV_ICON_BOX_SIZE,
  "height": NAV_ICON_BOX_SIZE,
  "display": "flex",
  "alignItems": "center",
  "justifyContent": "center",
  "svg": {
    width: NAV_ICON_GLYPH_SIZE,
    height: NAV_ICON_GLYPH_SIZE
  }
});

const ListItemContent = ({ navItem, open, navItemIsOpen, currentPage, showIcon, isChild = false }) => {
  const validationResults = useAppStore(validationResultsSelector);
  const hasKnowledgeErrors = useAppStore(hasKnowledgeValidationErrorsSelector);
  const hasKnowledgeSourceErrors = useAppStore(hasKnowledgeSourceValidationErrorsSelector);
  const systemMode = useAppStore(systemModeSelector);
  const isEditMode = systemMode === SYSTEM_MODES.EDIT;
  const hasValidationMessage = getValidationMessage(validationResults, navItem.key, VALIDATIONS_SEVERITY.ERROR);
  const showValidationBadge = Boolean(hasValidationMessage)
    || (navItem.key === KNOWLEDGE_SOURCES && isEditMode && (hasKnowledgeErrors || hasKnowledgeSourceErrors));
  const navigatesToOnlyChild = hasOnlyChild(navItem);
  const isItemSelected = currentPage.includes(navItem.key) && (!navItem.children || (navigatesToOnlyChild && !open));
  const togglesChildren = Boolean(navItem.children?.length) && open;

  return (
    <StyledListItemButton
      open={open}
      isChild={isChild}
      disableRipple={isChild || navigatesToOnlyChild}
      selected={isItemSelected}
      aria-current={isItemSelected ? "page" : undefined}
      aria-label={open ? undefined : navItem.text}
      aria-expanded={togglesChildren ? navItemIsOpen : undefined}
      aria-controls={togglesChildren ? NAVIGATION_DOM_IDS.itemChildrenRegion(navItem.key) : undefined}
      data-testid={NAVIGATION_TEST_IDS.item(navItem.text)}
    >
      {showIcon && (
        <StyledListItemIcon>
          {navItem.icon ? <navItem.icon /> : <SvgIcon />}
        </StyledListItemIcon>
      )}
      {open && (
        <>
          <HyroTruncateTypography variant="subtitle2" maxWidth={NAV_LABEL_MAX_WIDTH}>{navItem.text}</HyroTruncateTypography>
          {navItem?.isNew ? <StyledChip color={"secondary"} label={"New!"} /> : null}
          {navItem.children
            && (navItemIsOpen
              ? <ExpandLess sx={{ fontSize: NAV_ICON_GLYPH_SIZE }} />
              : <ExpandMore sx={{ fontSize: NAV_ICON_GLYPH_SIZE }} />)}
        </>
      )}
      {showValidationBadge && <MandatoryBadge close={!open} sx={{ ml: "auto" }} />}
    </StyledListItemButton>
  );
};

export default ListItemContent;
